import Vue from "vue";
import { mapMutations, mapGetters, mapActions } from "vuex";
import { email, maxLength, minLength, required, sameAs } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import { snakeCase, camelCase } from "lodash";

export default {
    name: "register-form",
    mixin: [validationMixin],
    data() {
        return {
            payload: {
                email: "",
                phone: "",
                password: "",
                password_confirmation: "",
                consent: false,
            },
            validationErrors: {},
            activeRegisterRadio: "mobile",
        };
    },
    validations() {
        if (this.activeRegisterRadio === "mobile") {
            return {
                payload: {
                    phone: {
                        required,
                        minLength: minLength(19),
                    },
                    password: {
                        required,
                        minLength: minLength(8),
                        maxLength: maxLength(20),
                    },
                    password_confirmation: {
                        maxLength: maxLength(20),
                        required,
                        sameAsPassword: sameAs("password"),
                    },
                    consent: {
                        sameAs: sameAs(() => true),
                    },
                },
            };
        } else {
            return {
                payload: {
                    email: {
                        required,
                        email,
                        maxLength: maxLength(50),
                    },
                    password: {
                        required,
                        minLength: minLength(8),
                        maxLength: maxLength(20),
                    },
                    password_confirmation: {
                        maxLength: maxLength(20),
                        required,
                        sameAsPassword: sameAs("password"),
                    },
                    consent: {
                        sameAs: sameAs(() => true),
                    },
                },
            };
        }
    },
    computed: {
        ...mapGetters({
            loadingRegistration: "auth/loadingRegistration",
            popupRegister: "auth/popupRegister",
            showPersonalData: "auth/showPersonalData",
        }),
        phoneErrors() {
            let error = [];
            if (!this.$v.payload.phone.$dirty) {
                return error;
            }
            if (!this.$v.payload.phone.required) {
                error.push(this.$t("validationRequired"));
            }
            if (!this.$v.payload.phone.minLength) {
                error.push(this.$t("validationLengthPhone"));
            }
            if (this.validationErrors.phone) {
                this.validationErrors.phone.forEach(row => {
                    error.push(row);
                });
            }
            return error;
        },
        emailErrors() {
            let error = [];
            if (!this.$v.payload.email.$dirty) {
                return error;
            }
            if (!this.$v.payload.email.maxLength) {
                error.push(this.$t("validationMax") + " 50");
            }
            if (!this.$v.payload.email.required) {
                error.push(this.$t("validationRequired"));
            }
            if (!this.$v.payload.email.email) {
                error.push(this.$t("validationEmail"));
            }
            if (this.validationErrors.email) {
                this.validationErrors.email.forEach(row => {
                    error.push(row);
                });
            }
            return error;
        },
        passwordErrors() {
            let error = [];
            if (!this.$v.payload.password.$dirty) {
                return error;
            }
            if (!this.$v.payload.password.maxLength) {
                error.push(this.$t("validationMax") + " 30");
            }
            if (!this.$v.payload.password.required) {
                error.push(this.$t("validationRequired"));
            }
            if (!this.$v.payload.password.minLength) {
                error.push(this.$t("validationMin").replace(":count", 8));
            }
            if (this.validationErrors.password) {
                this.validationErrors.password.forEach(row => {
                    error.push(row);
                });
            }
            return error;
        },
        passwordConfirmationErrors() {
            let error = [];
            if (!this.$v.payload.password_confirmation.$dirty) {
                return error;
            }
            if (!this.$v.payload.password_confirmation.required) {
                error.push(this.$t("validationRequired"));
            }
            if (!this.$v.payload.password_confirmation.sameAsPassword) {
                error.push(this.$t("validationSameAs"));
            }
            if (this.validationErrors.password_confirmation) {
                this.validationErrors.password_confirmation.forEach(row => {
                    error.push(row);
                });
            }
            return error;
        },
        acceptAgreementError() {
            let error = [];
            if (!this.$v.payload.consent.$dirty) {
                return error;
            }
            if (!this.$v.payload.consent.sameAs) {
                error.push(this.$t("validateAcceptAgreement"));
            }
            return error;
        },
    },
    methods: {
        ...mapActions({
            registration: `auth/REGISTRATION`,
            fetchUser: `profile/FETCH_USER_DATA`,
        }),
        ...mapMutations({
            changePopupRegister: `auth/CHANGE_POPUP_REGISTER`,
            changePersonalData: `auth/CHANGE_POPUP_PERSONAL_DATA`,
        }),
        submit() {
            if (this.activeRegisterRadio === "mobile") {
                this.$v.$touch();
                if (!this.$v.$invalid) {
                    let registerData = {};
                    registerData.phone = this.payload.phone.replace(/[\s.,%()-]/g, "");
                    registerData.password = this.payload.password;
                    registerData.password_confirmation = this.payload.password_confirmation;
                    this.registration(registerData)
                        .then(() => {
                            this.$toasted.success(this.$t("fillDataAfterRegister"));
                            this.changePopupRegister({ show: false });
                            this.fetchUser().then(() => {
                                setTimeout(() => {
                                    this.changePersonalData(true);
                                }, 100);
                            });
                        })
                        .catch(error => {
                            console.log(error.response);
                            if (error.response.status === 422) {
                                const errors = error.response.data.errors;
                                for (const i in errors) {
                                    Vue.set(this.validationErrors, camelCase(i), errors[i]);
                                    errors[i].forEach(e => {
                                        this.$toasted.error(e);
                                    });
                                }
                            }
                        });
                }
            } else {
                this.$v.$touch();
                if (!this.$v.$invalid) {
                    let registerData = {};
                    registerData.email = this.payload.email;
                    registerData.password = this.payload.password;
                    registerData.password_confirmation = this.payload.password_confirmation;
                    this.registration(registerData)
                        .then(() => {
                            this.$toasted.success(this.$t("fillDataAfterRegister"));
                            this.changePopupRegister({ show: false });
                            this.fetchUser().then(() => {
                                setTimeout(() => {
                                    this.changePersonalData(true);
                                }, 100);
                            });
                        })
                        .catch(error => {
                            if (error.response.status === 422) {
                                this.$toasted.error(this.$t("alreadyRegister"));
                                const errors = error.response.data.errors;
                                for (const i in errors) {
                                    Vue.set(this.validationErrors, camelCase(i), errors[i]);
                                    errors[i].forEach(e => {
                                        // this.$toasted.error(e);
                                    });
                                }
                            }
                        });
                }
            }
        },
        clearPayloadValue() {
            this.payload.email = "";
            this.payload.phone = "";
            this.payload.password = "";
            this.payload.password_confirmation = "";
        },
        clearFormEmail() {
            this.activeRegisterRadio = "mobile";
            this.$nextTick(() => {
                this.$v.$reset();
            });
            this.clearPayloadValue();
        },
        clearFormPhone() {
            this.activeRegisterRadio = "email";
            this.$nextTick(() => {
                this.$v.$reset();
            });
            this.clearPayloadValue();
        },
    },
};
